<template>
    <flex-container>
        <text-decorator bold :size="FontSize.xs">
            Vestigingen
        </text-decorator>
        <flex-container :gap="Size.sm">
            <back-button :to="{ name: 'brand-list' }" />
            <text-heading>
                <span v-text="t(`editBrandButton`)" />
            </text-heading>
        </flex-container>
        <flex-container
            :screen-sizes="{
                desktop: 7,
            }"
        >
            <card-normal without-padding>
                <flex-container>
                    <flex-container
                        :padding="Size.md"
                        :screen-sizes="{
                            laptop: 5,
                        }"
                    >
                        <text-description>
                            <span v-html="t(`editBrandDescription`)" />
                        </text-description>

                        <BrandForm ref="brandForm">
                            <template #additionalContent>
                                <horizontal-line no-margin />

                                <flex-container>
                                    <flex-container :gap="Size.sm">
                                        <text-heading>
                                            Embed code
                                        </text-heading>
                                        <text-description>
                                            Deze code is noodzakelijk om een AI Agent te kunnen implementeren.
                                            Plaats deze op je website of voeg hem toe aan Google Tag Manager.
                                        </text-description>
                                    </flex-container>

                                    <brand-copy-embed-textarea
                                        :brand-id="brandId"
                                    />
                                </flex-container>
                            </template>
                        </BrandForm>

                        <flex-container justify-content="flex-end" align-items="center">
                            <button-text @click="$router.push({ name: 'brand-list' })">
                                {{ t('button.cancel') }}
                            </button-text>
                            <button-block
                                :disabled="!isValid"
                                @click="save"
                            >
                                {{ t('button.save') }}
                            </button-block>
                        </flex-container>
                    </flex-container>

                    <flex-container
                        :screen-sizes="{
                            laptop: 5,
                        }"
                    >
                        <branding-preview
                            v-if="brandForm"
                            :values="brandForm.form"
                        />
                    </flex-container>
                </flex-container>
            </card-normal>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import BackButton from '@/shared/components/BackButton.vue';
    import { computed, onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import {
        FlexContainer,
        FontSize,
        NotificationBlock,
        Size,
        useConfiguration,
        useInteractionBlocker,
        useNotify,
    } from '@keyvue/one-ui';
    import BrandForm, { type ExposedBrandForm } from '@/company/brand/components/BrandForm.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { Brand, ChangeBrandCommand } from '@keyvue/one-api-client';
    import BrandChangedEvent, { type BrandChangedEventParameters } from '@/company/brand/events/BrandChangedEvent';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import BrandingPreview from '@/company/brand/components/BrandingPreview.vue';
    import BrandCopyEmbedTextarea from '@/company/brand/components/BrandCopyEmbedTextarea.vue';
    import { useI18n } from 'vue-i18n';
    import messages from './BrandEditView.messages.json';

    const { t } = useI18n({ messages });
    const { notify } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();
    const { client, currentCompany } = useClient();
    const router = useRouter();
    const { fontConfiguration } = useConfiguration();
    const eventBus = useEventBus();

    const brandForm = ref<ExposedBrandForm>();
    const validation = computed(() => brandForm.value?.formValidation.formValidationBindings);
    const isValid = computed<boolean>(() => validation.value?.isValid.value ?? false);
    const brandId = computed<string>(() => router.currentRoute.value.params.brandId.toString());

    onMounted(async () => {
        const brand = await client.get(Brand, brandId.value);

        brandForm.value?.setForm({
            name: brand.name,
            logo: brand.logo,
            primaryColor: brand.primaryColor ?? '#ff0000',
            secondaryColor: brand.secondaryColor ?? '#ffff00',
            textColor: brand.textColor ?? '#999999',
            linkColor: brand.linkColor ?? '#0000FF',
            buttonColor: brand.buttonColor ?? '#00ff00',
            buttonTextColor: brand.buttonTextColor ?? '#999999',
            headingFontName: fontConfiguration.fonts?.find((font: { name: string }) => font.name === brand.headingFontName) ?? null,
            fontName: fontConfiguration.fonts?.find((font: { name: string }) => font.name === brand.fontName) ?? null,
            googleTagId: brand.googleTagId ?? '',
            websiteUrl: brand.websiteUrl,
            additionalUrls: {
                sales: '',
                purchase: '',
                valuation: '',
                mortgage: '',
                contact: '',
                ...brand.additionalUrls,
            },
        });
    });

    async function save() {
        const clearBlock = blockInteractionForLoad(t('notification.interactionBlocker'));

        const formValues = brandForm.value?.form;

        if (!formValues) {
            clearBlock();

            notify({
                component: NotificationBlock,
                props: {
                    title: t('notification.error.title'),
                    text: t('notification.error.text'),
                    color: 'red-500',
                },
                duration: 4000,
            });

            return;
        }

        const command = new ChangeBrandCommand(
            currentCompany.value.id.value,
            brandId.value,
            formValues.name,
            formValues.logo?.id.value ?? '',
            formValues.primaryColor,
            formValues.secondaryColor,
            formValues.textColor,
            formValues.linkColor,
            formValues.buttonColor,
            formValues.buttonTextColor,
            formValues.headingFontName?.name ?? '',
            formValues.fontName?.name ?? '',
            formValues.googleTagId,
            formValues.websiteUrl,
            formValues.additionalUrls,
            true,
            null,
        );

        await client.dispatch(command);

        clearBlock();

        notify({
            component: NotificationBlock,
            props: {
                title: t('notification.success.title'),
                text: t('notification.success.text'),
                color: 'green-500',
            },
            duration: 4000,
        });

        eventBus.dispatch<BrandChangedEventParameters>(
            BrandChangedEvent,
            { brandId: brandId.value },
        );

        await router.push({ name: 'brand-list' });
    }
</script>
